import { useCallback, useMemo } from 'react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { usePRD3040_R01Store } from './Store_PRD3040_R01';
import { PRD3040_R01TabType } from './Store_PRD3040_R01';
import { getDateProductColumns, getItemProductColumns, getWorkplaceProductColumns } from './components/ColumnDefinitions';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { exportPRD3040_R01Excel } from './Api_PRD3040_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { PRD3040_R01_TABS, dedupeRowsByTab, getTabDisplayName, getViewType } from './utils/tabConfig';

// 훅으로 전환: 컨테이너에서 정상적인 훅 호출로 핸들러를 수급
interface WMS2010ContentProps {
    setMessage: (message: string) => void;
}

export const Content_PRD3040_R01 = (props: WMS2010ContentProps) => {
    const { setMessage } = props;
    

    const { 
        PRD3040_R01Filter, 
        PRD3040_R01activeTab, 
        PRD3040_R01tabData, 
        setPRD3040_R01ActiveTab, 
        setPRD3040_R01TabData, 
        setPRD3040_R01TabSelectedIds, 
        setPRD3040_R01TabLoadingState,
        resetAllPRD3040_R01TabScrollTop,
        setPRD3040_R01TabScrollTop,
    } = usePRD3040_R01Store();

	const { unitList } = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

    // 활성 탭에 따른 데이터/컬럼 매핑 - 생산실적 조회 규격
    const { columns, data } = useMemo(() => {
        switch (PRD3040_R01activeTab) {
            case 'item':
                return { columns: getItemProductColumns(unitList), data: PRD3040_R01tabData.item };
            case 'workplace':
                return { columns: getWorkplaceProductColumns(unitList), data: PRD3040_R01tabData.workplace };
            case 'date':
                return { columns: getDateProductColumns(unitList), data: PRD3040_R01tabData.date };
            default:
                return { columns: getDateProductColumns(unitList), data: PRD3040_R01tabData.date };
        }
    }, [PRD3040_R01activeTab, PRD3040_R01tabData.date, PRD3040_R01tabData.item, PRD3040_R01tabData.workplace]);

    // 탭 변경 시 상태 초기화 
    const changeTabAndFetch = useCallback((tab: PRD3040_R01TabType, options: { clearData?: boolean; clearSelection?: boolean } = {}) => {
        const { clearData = true, clearSelection = true } = options;
        
        // 활성 탭 변경
        setPRD3040_R01ActiveTab(tab);
        
        // 데이터 및 선택 상태 초기화
        if (clearData) {
            setPRD3040_R01TabData(tab, []);
        }
        if (clearSelection) {
            setPRD3040_R01TabSelectedIds(tab, []);
        }
    }, [setPRD3040_R01ActiveTab, setPRD3040_R01TabData, setPRD3040_R01TabSelectedIds]);

    // 전체 탭 병렬 조회 
    const fetchData = useCallback(async () => {
        resetAllPRD3040_R01TabScrollTop();
        const tabs: PRD3040_R01TabType[] = PRD3040_R01_TABS.map(tab => tab.id);

        const { getPRD3040_R01Data } = await import('./Api_PRD3040_R01');

        // 각 탭별 응답 건수를 저장할 배열
        const tabCounts: { tab: PRD3040_R01TabType; count: number }[] = [];

        await Promise.all(tabs.map(async (tab) => {
            try {
                setPRD3040_R01TabLoadingState(tab, true);

                const viewType = getViewType(tab);                
                const res = await getPRD3040_R01Data(viewType, PRD3040_R01Filter);

                // 데이터 검증 및 중복 제거 (행 ID 기준) - 생산실적 규격
                if (res?.data?.data && Array.isArray(res.data.data)) {
                    const validData = res.data.data.filter((item: any) => item && typeof item === 'object');

                    const uniqueData = dedupeRowsByTab(tab, validData);

                    if (uniqueData.length > 0) {
                        setPRD3040_R01TabData(tab, uniqueData);
                        tabCounts.push({ tab, count: uniqueData.length });
                    } else {
                        console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                        setPRD3040_R01TabData(tab, []);
                        tabCounts.push({ tab, count: 0 });
                    }
                } else {
                    console.warn(`${tab} 탭: 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                    setPRD3040_R01TabData(tab, []);
                    tabCounts.push({ tab, count: 0 });
                }
            } catch (e) {
                console.error(`${tab} 탭 데이터 조회 실패:`, e);
                setPRD3040_R01TabData(tab, []);
                tabCounts.push({ tab, count: 0 });
            } finally {
                setPRD3040_R01TabLoadingState(tab, false);
            }
        }));
        
        // 각 탭별 건수 표시
        const totalCount = tabCounts.reduce((sum, item) => sum + item.count, 0);

		if(totalCount > 0) {
			ShowSuccessFetch(setMessage, `생산실적 조회가 완료되었습니다. (총 ${totalCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
		}

    }, [getViewType, setPRD3040_R01TabData, setPRD3040_R01TabLoadingState, PRD3040_R01Filter, getTabDisplayName, setMessage, resetAllPRD3040_R01TabScrollTop]);

    // 개별 탭 조회 함수
    const fetchSingleTabData = useCallback(async (tab: PRD3040_R01TabType) => {
        setPRD3040_R01TabScrollTop(tab, null);
        setLoadingSpinner(true);
        try {
            
            setPRD3040_R01TabLoadingState(tab, true);

            const { getPRD3040_R01Data } = await import('./Api_PRD3040_R01');
            const viewType = getViewType(tab);
            const res: any = await getPRD3040_R01Data(viewType, PRD3040_R01Filter);

            // 데이터 검증 및 처리
            if (res?.data?.data && Array.isArray(res.data.data)) {
                const validData = res.data.data.filter((item: any) => item && typeof item === 'object');

                // 중복 제거 (행 ID 기준)
                const uniqueData = dedupeRowsByTab(tab, validData);

                if (uniqueData.length > 0) {
                    setPRD3040_R01TabData(tab, uniqueData);
					ShowSuccessFetch(setMessage, `${getTabDisplayName(tab)} 조회가 완료되었습니다. (총 ${uniqueData.length}건)`);
					setTimeout(() => setMessage(""), 5000);
                } else {
                    console.warn(`${tab} 탭: 처리 후 유효한 데이터가 없습니다.`);
                    setPRD3040_R01TabData(tab, []);
					showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
                }
            } else {
                console.warn(`${tab} 탭: API 응답 데이터가 없거나 잘못된 형식입니다.`, res);
                setPRD3040_R01TabData(tab, []);
				showWarning(`${getTabDisplayName(tab)} 데이터가 없습니다.`);
            }
        } catch (e) {
            console.error(`${tab} 탭 데이터 조회 실패:`, e);
            setPRD3040_R01TabData(tab, []);
            showWarning(`${getTabDisplayName(tab)} 조회 중 오류가 발생했습니다.`);
        } finally {
            setPRD3040_R01TabLoadingState(tab, false);
            setLoadingSpinner(false);
        }
    }, [getViewType, setPRD3040_R01TabData, setPRD3040_R01TabLoadingState, PRD3040_R01Filter, getTabDisplayName, setMessage, setPRD3040_R01TabScrollTop, setLoadingSpinner]);

    // 탭 변경 시 데이터 조회 처리 - 항상 조회
    const handleTabChange = useCallback(async (tab: PRD3040_R01TabType) => {
        try {
            // 1. 탭 변경 및 상태 초기화
            changeTabAndFetch(tab, { clearData: true, clearSelection: true });
            
            // 2. 활성 탭 항상 조회 (데이터가 있어도 최신 데이터로 갱신)
            await fetchSingleTabData(tab);
        } catch (error) {
            console.error('탭 변경 중 오류:', error);
        }
    }, [changeTabAndFetch, fetchSingleTabData]);

    // Excel 내보내기 핸들러
    const handleExcelClick = useCallback(async () => {
        try {
            const state = usePRD3040_R01Store.getState();
            const activeTab = state.PRD3040_R01activeTab;
            const filter = state.PRD3040_R01Filter;

            // 뷰 타입 변환
            const viewType = getViewType(activeTab);
            // API 호출
            const response = await exportPRD3040_R01Excel(viewType, { ...filter });

            // Base64 응답 처리
            if (response?.data?.data) {
                // 공통 유틸리티 사용
                downloadSettingExcel({ 
                    responseData: response.data.data, 
                    fileName: `생산실적조회_${getTabDisplayName(activeTab)}.xlsx`
                });
            } else {
                throw new Error('서버에서 올바른 엑셀 데이터를 받지 못했습니다.');
            }
            
        } catch (error) {
            console.error('Excel 내보내기 실패:', error);
            
            // 사용자에게 에러 알림
            if (error instanceof Error) {
                showWarning(`엑셀 내보내기 실패: ${error.message}`);
            } else {
                showWarning('엑셀 내보내기 중 오류가 발생했습니다.');
            }
        }
    }, [getViewType, getTabDisplayName]);

    return { 
        fetchData, 
        handleExcelClick, 
        handleTabChange,
        columns,
        data
    };
};
